import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getDisponibilidadeFrotaPatioData, getPosicoesSalvasPorPlacasData } from '../services/disponibilidadeFrotaPatioApi';
import {
  clearPosicoesCache,
  getValidDisponibilidadeCache,
  getValidPosicoesCache,
  readDisponibilidadeCache,
  saveDisponibilidadeCache,
  savePosicoesCache
} from '../services/disponibilidadeFrotaPatioCache';
import {
  allRegionsOption,
  orderedRegionLabels,
  positionRefreshIntervalMs,
  positionRequestConfig
} from '../utils/constants';
import {
  getRemainingPositionRefreshMs
} from '../utils/formatters';
import {
  buildCategoriaDisponivelFilterOverrides,
  buildDadosDisponibilidade
} from '../utils/mappers';
import {
  createLoadingPositionEntry,
  createMissingPositionEntry,
  buildCachedPositionEntry,
  enrichDisponibilidadeWithPositions,
  findPositionBatchItemByPlate,
  getPlateForPosition,
  getPositionEntryFromMap,
  getPositionLocalFromPayload,
  getPositionQueryTargets,
  getPositionStatusForItem,
  getRegionLabelFromState,
  getScopedPositionMap,
  hasValidCoordinates,
  isFinalPositionEntry,
  mergePositionEntryIntoMap
} from '../utils/positions';
import {
  filtrarPorCategoria,
  getCategoriaPainel,
  isSameSelection
} from '../utils/selection';
import { exactMatchFilter } from '../utils/filters';

export const useDisponibilidadeFrotaPatioController = ({ enableMap = true } = {}) => {
  const initialCachedDisponibilidade = useMemo(() => getValidDisponibilidadeCache(), []);
  const initialCachedPosicoes = useMemo(() => getValidPosicoesCache(), []);
  const [categoriaAtiva, setCategoriaAtiva] = useState(null);
  const [resetFiltrosCategoriaToken, setResetFiltrosCategoriaToken] = useState(0);
  const [selecaoAtiva, setSelecaoAtiva] = useState(null);
  const [apiData, setApiData] = useState(() => initialCachedDisponibilidade?.data || null);
  const [posicoesPorChave, setPosicoesPorChave] = useState(() => initialCachedPosicoes?.posicoesPorChave || {});
  const [posicoesPorChaveProcessando, setPosicoesPorChaveProcessando] = useState(() => initialCachedPosicoes?.posicoesPorChave || {});
  const [apiLoading, setApiLoading] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(() => initialCachedDisponibilidade?.updatedAt || null);
  const [apiRefreshKey, setApiRefreshKey] = useState(0);
  const [selectedMapRegion, setSelectedMapRegion] = useState(allRegionsOption);
  const [selectedMapFrotaSearch, setSelectedMapFrotaSearch] = useState('');
  const [positionQueryTargets, setPositionQueryTargets] = useState(() => (
    enableMap ? getPositionQueryTargets(initialCachedDisponibilidade?.data?.frotasTracao || []) : []
  ));
  const [positionLastUpdatedAt, setPositionLastUpdatedAt] = useState(() => initialCachedPosicoes?.updatedAt || null);
  const [positionRefreshTick, setPositionRefreshTick] = useState(0);
  const [positionRefreshCountdownMs, setPositionRefreshCountdownMs] = useState(() => (
    getRemainingPositionRefreshMs(initialCachedPosicoes?.dataAtualizacao || initialCachedPosicoes?.updatedAt || null)
  ));
  const [positionRefreshPaused, setPositionRefreshPaused] = useState(false);
  const [positionLoading, setPositionLoading] = useState(false);
  const activePositionHydrationRef = useRef(0);
  const posicoesPorChaveRef = useRef(initialCachedPosicoes?.posicoesPorChave || {});
  const positionQueryTargetsRef = useRef(getPositionQueryTargets(initialCachedDisponibilidade?.data?.frotasTracao || []));
  const lastPositionRefreshTickRef = useRef(0);

  const handleApiRefresh = useCallback(() => {
    setApiRefreshKey(prev => prev + 1);
  }, []);

  const handlePositionRefresh = useCallback(() => {
    setPositionRefreshCountdownMs(positionRefreshIntervalMs);
    setPositionRefreshTick(prev => prev + 1);
  }, []);

  const handleTogglePositionRefreshPaused = useCallback(() => {
    setPositionRefreshPaused(prev => !prev);
  }, []);

  const handlePosicaoLoaded = useCallback((item, entry) => {
    const next = mergePositionEntryIntoMap(posicoesPorChaveRef.current || {}, item, entry);
    setPosicoesPorChave(next);
    setPosicoesPorChaveProcessando(next);
    posicoesPorChaveRef.current = next;
  }, []);

  const handleVisibleTracaoItemsChange = useCallback(() => {}, []);

  const handleToggleSelection = useCallback((tipo, item) => {
    setSelecaoAtiva(prev => {
      if (prev && prev.tipo !== tipo) return prev;
      return isSameSelection(prev, tipo, item) ? null : { tipo, item };
    });
  }, []);

  useEffect(() => {
    posicoesPorChaveRef.current = posicoesPorChave;
  }, [posicoesPorChave]);

  useEffect(() => {
    positionQueryTargetsRef.current = enableMap ? positionQueryTargets : [];
  }, [enableMap, positionQueryTargets]);

  useEffect(() => {
    let active = true;

    const carregarDisponibilidadeApi = async () => {
      const cachedDisponibilidade = apiRefreshKey === 0 ? getValidDisponibilidadeCache() : null;
      const cachedDisponibilidadeFallback = readDisponibilidadeCache();

      if (cachedDisponibilidade) {
        if (active) {
          setApiData(cachedDisponibilidade.data);
          setPositionQueryTargets(enableMap ? getPositionQueryTargets(cachedDisponibilidade.data?.frotasTracao || []) : []);
          setLastUpdatedAt(cachedDisponibilidade.updatedAt);
          setApiLoading(false);
        }

        return;
      }

      setApiLoading(true);

      try {
        const requestPayload = apiRefreshKey > 0 ? { recarregar: true } : undefined;
        const response = await getDisponibilidadeFrotaPatioData(requestPayload);
        const processedResponse = buildDadosDisponibilidade(response);
        const cachedAt = saveDisponibilidadeCache(processedResponse);
        const updatedAt = cachedAt ? new Date(cachedAt) : new Date();

        if (active) {
          setApiData(processedResponse);
          setPositionQueryTargets(enableMap ? getPositionQueryTargets(processedResponse.frotasTracao) : []);
          setLastUpdatedAt(updatedAt);
        }
      } catch (error) {
        console.error('Erro ao carregar disponibilidade frota patio:', error);

        if (active) {
          if (cachedDisponibilidadeFallback?.data) {
            setApiData(cachedDisponibilidadeFallback.data);
            setPositionQueryTargets(enableMap ? getPositionQueryTargets(cachedDisponibilidadeFallback.data?.frotasTracao || []) : []);
            setLastUpdatedAt(cachedDisponibilidadeFallback.updatedAt);
          } else {
            const emptyData = buildDadosDisponibilidade(null);
            setApiData(emptyData);
            setPosicoesPorChave({});
            setPosicoesPorChaveProcessando({});
            posicoesPorChaveRef.current = {};
            clearPosicoesCache();
            setPositionQueryTargets([]);
          }
        }
      } finally {
        if (active) {
          setApiLoading(false);
        }
      }
    };

    carregarDisponibilidadeApi();

    return () => {
      active = false;
    };
  }, [apiRefreshKey, enableMap]);

  useEffect(() => {
    if (!enableMap || typeof window === 'undefined' || !positionQueryTargets.length || positionRefreshPaused) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setPositionRefreshCountdownMs(prev => Math.max(prev - 1000, 0));
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [enableMap, positionQueryTargets.length, positionRefreshPaused]);

  useEffect(() => {
    if (!enableMap || !positionQueryTargets.length || positionRefreshPaused || positionRefreshCountdownMs > 0) {
      return;
    }

    setPositionRefreshCountdownMs(positionRefreshIntervalMs);
    setPositionRefreshTick(prev => prev + 1);
  }, [enableMap, positionQueryTargets.length, positionRefreshCountdownMs, positionRefreshPaused]);

  useEffect(() => {
    const targets = enableMap ? positionQueryTargets : [];
    const forceRefresh = positionRefreshTick > 0 && lastPositionRefreshTickRef.current !== positionRefreshTick;

    lastPositionRefreshTickRef.current = positionRefreshTick;

    activePositionHydrationRef.current += 1;
    const requestId = activePositionHydrationRef.current;

    if (!targets.length) {
      if (apiData) {
        setPosicoesPorChave({});
        setPosicoesPorChaveProcessando({});
        posicoesPorChaveRef.current = {};
        setPositionLastUpdatedAt(null);
        setPositionRefreshCountdownMs(positionRefreshIntervalMs);
        setPositionLoading(false);
        clearPosicoesCache();
      }
      return undefined;
    }

    const currentMap = posicoesPorChaveRef.current || {};
    const nextBaseMap = targets.reduce((nextMap, item) => {
      const currentEntry = getPositionEntryFromMap(currentMap, item);

      if (currentEntry && isFinalPositionEntry(currentEntry) && !forceRefresh) {
        return mergePositionEntryIntoMap(nextMap, item, currentEntry);
      }

      return mergePositionEntryIntoMap(nextMap, item, createLoadingPositionEntry());
    }, {});

    setPosicoesPorChaveProcessando(nextBaseMap);

    const targetsToFetch = forceRefresh
      ? targets
      : targets.filter(item => !isFinalPositionEntry(getPositionEntryFromMap(currentMap, item)));

    if (!targetsToFetch.length) {
      return undefined;
    }

    let workingMap = nextBaseMap;

    const syncProcessingMap = nextMap => {
      const scopedMap = getScopedPositionMap(nextMap, targets);
      setPosicoesPorChaveProcessando(scopedMap);
      return scopedMap;
    };

    const hydratePositions = async () => {
      const placas = targetsToFetch
        .map(item => getPlateForPosition(item))
        .filter(Boolean);

      setPositionLoading(true);

      if (!placas.length) {
        const nextMap = targetsToFetch.reduce((acc, item) => (
          mergePositionEntryIntoMap(acc, item, createMissingPositionEntry())
        ), workingMap);

        if (requestId !== activePositionHydrationRef.current) return;

        const scopedMap = syncProcessingMap(nextMap);
        setPosicoesPorChave(scopedMap);
        posicoesPorChaveRef.current = scopedMap;
        const cachedAt = savePosicoesCache(scopedMap);
        setPositionLastUpdatedAt(cachedAt ? new Date(cachedAt) : new Date());
        setPositionRefreshCountdownMs(positionRefreshIntervalMs);
        setPositionLoading(false);
        return;
      }

      try {
        const response = await getPosicoesSalvasPorPlacasData(placas, positionRequestConfig);

        if (requestId !== activePositionHydrationRef.current) return;

        targetsToFetch.forEach(item => {
          const placa = getPlateForPosition(item);
          const nextEntry = placa
            ? buildCachedPositionEntry(
              findPositionBatchItemByPlate(response, placa) || { placa, encontrada: false },
              placa
            )
            : createMissingPositionEntry();

          workingMap = mergePositionEntryIntoMap(workingMap, item, nextEntry);
        });

        const scopedMap = syncProcessingMap(workingMap);
        setPosicoesPorChave(scopedMap);
        posicoesPorChaveRef.current = scopedMap;
        const cachedAt = savePosicoesCache(scopedMap);
        setPositionLastUpdatedAt(cachedAt ? new Date(cachedAt) : new Date());
        setPositionRefreshCountdownMs(positionRefreshIntervalMs);
        setPositionLoading(false);
      } catch (error) {
        if (requestId !== activePositionHydrationRef.current) return;

        const nextMap = targetsToFetch.reduce((acc, item) => (
          mergePositionEntryIntoMap(acc, item, createMissingPositionEntry())
        ), workingMap);
        const scopedMap = syncProcessingMap(nextMap);

        setPosicoesPorChave(scopedMap);
        posicoesPorChaveRef.current = scopedMap;
        const cachedAt = savePosicoesCache(scopedMap);
        setPositionLastUpdatedAt(cachedAt ? new Date(cachedAt) : new Date());
        setPositionRefreshCountdownMs(positionRefreshIntervalMs);
        setPositionLoading(false);
        console.error('Erro ao hidratar posicoes da disponibilidade frota patio:', error);
      }
    };

    hydratePositions();

    return () => {
      activePositionHydrationRef.current += 1;
    };
  }, [apiData, enableMap, positionQueryTargets, positionRefreshTick]);

  const shouldShowApiLoading = apiLoading && !apiData;

  const dadosDisponibilidade = useMemo(
    () => enrichDisponibilidadeWithPositions(apiData || buildDadosDisponibilidade(null), posicoesPorChave),
    [apiData, posicoesPorChave]
  );
  const categoriaTracaoAtiva = useMemo(() => getCategoriaPainel(categoriaAtiva, 'tracao'), [categoriaAtiva]);
  const categoriaReboqueAtiva = useMemo(() => getCategoriaPainel(categoriaAtiva, 'reboque'), [categoriaAtiva]);
  const frotasTracaoFiltradas = useMemo(
    () => filtrarPorCategoria(dadosDisponibilidade.frotasTracao, categoriaTracaoAtiva),
    [dadosDisponibilidade.frotasTracao, categoriaTracaoAtiva]
  );
  const frotasReboqueFiltradas = useMemo(
    () => filtrarPorCategoria(dadosDisponibilidade.frotasReboque, categoriaReboqueAtiva),
    [dadosDisponibilidade.frotasReboque, categoriaReboqueAtiva]
  );
  const categoriaDisponivelFilterOverridesTracao = useMemo(
    () => buildCategoriaDisponivelFilterOverrides(categoriaTracaoAtiva),
    [categoriaTracaoAtiva]
  );
  const categoriaDisponivelFilterOverridesReboque = useMemo(
    () => buildCategoriaDisponivelFilterOverrides(categoriaReboqueAtiva),
    [categoriaReboqueAtiva]
  );
  const mapaPosicoesFrotas = useMemo(() => {
    const tracoes = dadosDisponibilidade.frotasTracao || [];
    const positionMap = dadosDisponibilidade.posicoesPorChave || {};
    const processingPositionMap = posicoesPorChaveProcessando || {};
    const selectedRegionValue = selectedMapRegion?.value || '';
    const selectedFrotaValue = String(selectedMapFrotaSearch || '').trim();

    const items = tracoes.map(item => {
      const entry = getPositionEntryFromMap(positionMap, item);
      const posicao = item?.posicao || entry?.posicao || null;
      const latitude = Number(posicao?.latitude);
      const longitude = Number(posicao?.longitude);
      const local = posicao?.local || entry?.local || getPositionLocalFromPayload(posicao || {}) || '';
      const status = getPositionStatusForItem(item, processingPositionMap);
      const estado = posicao?.estado || '';
      const regiao = getRegionLabelFromState(estado);

      return {
        frota: item?.frota || '-',
        placa: posicao?.placa || item?.placa || item?.veiculo || '-',
        veiculo: item?.veiculo || item?.placa || '-',
        gestor: item?.gestor || '-',
        motorista: item?.motorista || '-',
        tipoModelo: item?.tipoModelo || item?.modeloVeicular || '-',
        modeloVeicular: item?.modeloVeicular || '-',
        segmentoVeiculo: item?.segmentoVeiculo || '-',
        tipoOperacao: item?.tipoOperacao || '-',
        cargasResumo: item?.cargasResumo || '-',
        disponivel: item?.disponivel === true,
        disponivelLabel: item?.disponivelLabel || '-',
        statusPatioLabel: item?.statusPatioLabel || '-',
        statusPatioVariant: item?.statusPatioVariant || 'secondary',
        dataPosicao: posicao?.dataPosicao || entry?.dataPosicao || null,
        cidade: posicao?.cidade || '',
        estado,
        regiao,
        integracao: posicao?.integracao ?? null,
        local,
        latitude,
        longitude,
        status
      };
    });

    const regionOptions = [
      allRegionsOption,
      ...orderedRegionLabels
        .filter(region => items.some(item => item.regiao === region))
        .map(region => ({
          value: region,
          label: region
        }))
    ];
    const frotaOptions = Array.from(
      new Set(
        items
          .map(item => String(item?.frota || '').trim())
          .filter(Boolean)
      )
    )
      .sort((a, b) => a.localeCompare(b, 'pt-BR'))
      .map(value => ({
        value,
        label: value
      }));

    const filteredItems = selectedRegionValue
      ? items.filter(item => item.regiao === selectedRegionValue)
      : items;
    const filteredByFrotaItems = selectedFrotaValue
      ? filteredItems.filter(item => exactMatchFilter(item.frota, selectedFrotaValue))
      : filteredItems;

    return {
      regionOptions,
      frotaOptions,
      allItems: filteredByFrotaItems,
      vehicles: filteredByFrotaItems.filter(item => hasValidCoordinates(item.latitude, item.longitude)),
      foundCount: filteredByFrotaItems.filter(item => hasValidCoordinates(item.latitude, item.longitude)).length,
      loadingCount: filteredByFrotaItems.filter(item => item.status === 'loading' || item.status === 'unknown').length,
      missingCount: filteredByFrotaItems.filter(item => item.status === 'missing').length
    };
  }, [
    dadosDisponibilidade.frotasTracao,
    dadosDisponibilidade.posicoesPorChave,
    posicoesPorChaveProcessando,
    selectedMapRegion,
    selectedMapFrotaSearch
  ]);

  const handleCategoriaAtivaChange = useCallback(value => {
    setSelecaoAtiva(null);
    setCategoriaAtiva(value || null);
    setResetFiltrosCategoriaToken(prev => prev + 1);
  }, []);

  const handleMapRegionChange = useCallback(option => {
    setSelectedMapRegion(option || allRegionsOption);
  }, []);

  const handleMapFrotaSearchChange = useCallback(value => {
    setSelectedMapFrotaSearch(value || '');
  }, []);

  useEffect(() => {
    const selectedRegionValue = selectedMapRegion?.value || '';

    if (!selectedRegionValue) return;

    const hasSelectedRegion = mapaPosicoesFrotas.regionOptions
      .some(option => option.value === selectedRegionValue);

    if (!hasSelectedRegion) {
      setSelectedMapRegion(allRegionsOption);
    }
  }, [mapaPosicoesFrotas.regionOptions, selectedMapRegion]);

  return {
    dadosDisponibilidade,
    apiLoading,
    shouldShowApiLoading,
    lastUpdatedAt,
    handleApiRefresh,
    categoriaAtiva,
    setCategoriaAtiva,
    resetFiltrosCategoriaToken,
    handleCategoriaAtivaChange,
    selecaoAtiva,
    handleToggleSelection,
    selectedMapRegion,
    handleMapRegionChange,
    selectedMapFrotaSearch,
    handleMapFrotaSearchChange,
    positionRefreshCountdownMs,
    positionLastUpdatedAt,
    positionRefreshPaused,
    positionLoading,
    handlePositionRefresh,
    handleTogglePositionRefreshPaused,
    mapaPosicoesFrotas,
    handlePosicaoLoaded,
    handleVisibleTracaoItemsChange,
    frotasTracaoFiltradas,
    frotasReboqueFiltradas,
    categoriaDisponivelFilterOverridesTracao,
    categoriaDisponivelFilterOverridesReboque
  };
};
